import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  SchoolTeacherClassChangeRequestsApi,
  TeacherClassChangeRequest,
} from '@/services/teacher-class-change-requests.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Select, Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { usePermissions } from '@/hooks/usePermissions';

const STATUS_TONE = { pending: 'amber', approved: 'green', rejected: 'red', partial_failure: 'red' } as const;
const STATUS_LABEL = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', partial_failure: 'Needs attention' } as const;

/**
 * Teacher Class Change Request — school tier. Covers both same-school
 * (reassigning which class a teacher teaches) and cross-school (moving a
 * teacher to a sibling org school) — the "New School" picker includes
 * your own school for the common same-school case. Approval happens at
 * the org console, never here.
 */
export function TeacherClassChangeRequestsPage() {
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canRequest = can('/staff', 'create');

  const [teacherId, setTeacherId] = useState('');
  const [newSchoolId, setNewSchoolId] = useState('');
  const [newClassId, setNewClassId] = useState('');
  const [newSectionId, setNewSectionId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: requests = [], isFetching } = useQuery({
    queryKey: ['school-teacher-class-change-requests'],
    queryFn: SchoolTeacherClassChangeRequestsApi.list,
  });

  const { data: teachers = [] } = useQuery({
    queryKey: ['teacher-class-change-teachers'],
    queryFn: SchoolTeacherClassChangeRequestsApi.teachers,
  });

  const { data: targetSchools = [] } = useQuery({
    queryKey: ['teacher-class-change-target-schools'],
    queryFn: SchoolTeacherClassChangeRequestsApi.targetSchools,
  });

  const { data: targetClasses = [] } = useQuery({
    queryKey: ['teacher-class-change-target-classes', newSchoolId],
    queryFn: () => SchoolTeacherClassChangeRequestsApi.targetClasses(newSchoolId),
    enabled: !!newSchoolId,
  });
  const targetSections = targetClasses.find((c) => c.id === newClassId)?.sections ?? [];

  const resetForm = () => {
    setTeacherId('');
    setNewSchoolId('');
    setNewClassId('');
    setNewSectionId('');
    setReason('');
  };

  const create = useMutation({
    mutationFn: () =>
      SchoolTeacherClassChangeRequestsApi.create({
        teacherId,
        newSchoolId,
        newSectionId,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['school-teacher-class-change-requests'] });
      resetForm();
    },
    onError: (e: any) => setError(e?.response?.data?.error?.message ?? 'Failed to submit request'),
  });

  const withdraw = useMutation({
    mutationFn: (id: string) => SchoolTeacherClassChangeRequestsApi.withdraw(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['school-teacher-class-change-requests'] }),
    onError: (e: any) => setError(e?.response?.data?.error?.message ?? 'Failed to withdraw request'),
  });

  const ready = !!teacherId && !!newSchoolId && !!newSectionId;

  return (
    <div>
      <PageHeader
        title="Teacher Class Change Requests"
        description="Request reassigning which class a teacher teaches — at your own school, or a move to a sibling school in your organization. An org admin approves before anything changes."
      />

      {error && (
        <div className="mb-4 flex items-center justify-between rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            &times;
          </button>
        </div>
      )}

      {canRequest && (
        <div className="mb-6 space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700">New Request</h3>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Teacher" required>
              <Select value={teacherId} onChange={(e) => setTeacherId(e.target.value)}>
                <option value="">Select</option>
                {teachers.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.employeeId})
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="New School" required hint="Includes your own school, for a same-school class reassignment.">
              <Select
                value={newSchoolId}
                onChange={(e) => {
                  setNewSchoolId(e.target.value);
                  setNewClassId('');
                  setNewSectionId('');
                }}
              >
                <option value="">Select</option>
                {targetSchools.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="New Class" required>
              <Select
                value={newClassId}
                onChange={(e) => {
                  setNewClassId(e.target.value);
                  setNewSectionId('');
                }}
                disabled={!newSchoolId}
              >
                <option value="">Select</option>
                {targetClasses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="New Section" required>
              <Select value={newSectionId} onChange={(e) => setNewSectionId(e.target.value)} disabled={!newClassId}>
                <option value="">Select</option>
                {targetSections.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <Field label="Reason" hint="Optional — helps the approving admin.">
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
          </Field>

          <div className="flex justify-end">
            <button
              type="button"
              className="btn-primary"
              disabled={!ready || create.isPending}
              onClick={() => {
                setError(null);
                create.mutate();
              }}
            >
              {create.isPending ? <Loader2 className="mr-1.5 inline h-4 w-4 animate-spin" /> : null}
              Submit Request
            </button>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Teacher</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">New Class</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Remarks</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isFetching ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-400">
                  Loading…
                </td>
              </tr>
            ) : requests.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-400">
                  No teacher class change requests yet.
                </td>
              </tr>
            ) : (
              requests.map((r: TeacherClassChangeRequest) => (
                <tr key={r.id}>
                  <td className="px-4 py-2.5 text-sm text-slate-800">{r.teacherName}</td>
                  <td className="px-4 py-2.5 text-sm text-slate-600">
                    {r.newClassName} / {r.newSectionName}
                    {r.currentSchoolId !== r.newSchoolId && <span className="ml-1 text-xs text-slate-400">(cross-school)</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-500">{r.remarks || r.partialFailureNote || '—'}</td>
                  <td className="px-4 py-2.5 text-right">
                    {r.status === 'pending' && canRequest && (
                      <button
                        type="button"
                        className="text-sm text-red-500 hover:text-red-700"
                        disabled={withdraw.isPending}
                        onClick={() => {
                          setError(null);
                          withdraw.mutate(r.id);
                        }}
                      >
                        Withdraw
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
